import { createContext } from '@/utils';
import type { DecoratedMagicChat } from '../../types';

export type ChatContextType = {
  chat: DecoratedMagicChat;
  // True while the getChat query is in flight (initial fetch or refetch);
  // always false when the query is disabled or no provider is mounted.
  isFetching: boolean;
  /**
   * Whether the server is known to have a `Chat` row for `chatId`. Chat ids
   * are minted client-side, so anything that asks the server to resolve the
   * chat relation (creating a magic job bound to the chat, repeating one) must
   * check first — an unknown id fails server-side rather than being created.
   */
  isChatPersisted: (chatId: string) => boolean;
  reset: () => void;
  addAttachment: (url: string, file: File) => void;
  attachments: Record<string, File>;
  setChat: (chat: DecoratedMagicChat) => void;
  timeZone?: string;
  /**
   * The experience a panel chat edits. Sent with every quick-edits turn so the
   * server can bind the chat to that page's collection on the first message —
   * `chat.experienceId` can't be used, because a locally minted chat has none
   * until a `getChat` lands, which is exactly when it's needed.
   */
  experienceId?: string;
  /**
   * Whether this chat writes straight into `experienceId`'s collection. True
   * for quick edits, whose tools edit the live page; false for add content,
   * which stages events in a collection of its own and publishes them through
   * `importChat`. Sent when this surface creates the chat row itself (an
   * attachment before any message), because that door does not carry the
   * streaming turn's `context`.
   */
  sharesExperienceCollection?: boolean;
};

export type ChatInternalContextType = {
  setSavedChatId: (id: string | null) => void;
  /**
   * Record that the server now has a `Chat` row for this id, without making it
   * the chat this provider is streaming. For callers that create a chat
   * directly rather than by streaming to it.
   */
  markChatPersisted: (chatId: string) => void;
};

// Some surfaces mount chat consumers without a provider (the extension and the
// share page mount the auth modal with no ChatContextProvider above it), so the
// callbacks in the fallback value have to be safe to invoke. With no provider
// there's no chat at all, so `isChatPersisted` denies everything.
const fallbackContext: Partial<ChatContextType> = {
  reset: () => void 0,
  setChat: () => void 0,
  isFetching: false,
  isChatPersisted: () => false,
};

export const ChatContext = createContext<ChatContextType>(
  fallbackContext as ChatContextType,
  'ChatContext'
);

export const ChatInternalContext = createContext<ChatInternalContextType>(
  {
    setSavedChatId: () => void 0,
    markChatPersisted: () => void 0,
  },
  'ChatInternalContext'
);
